import { useState, useRef, useCallback, useEffect } from 'react';
import config from '../config/environment';

const useCaroGame = () => {
  const [gameState, setGameState] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  const [playerColor, setPlayerColor] = useState(null);
  const [currentTurn, setCurrentTurn] = useState('X');
  const [gameStatus, setGameStatus] = useState({ isGameOver: false });
  const [lastMove, setLastMove] = useState(null); // Track last move
  const [players, setPlayers] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [playerId, setPlayerId] = useState(null);
  const [boardSize, setBoardSize] = useState(15);
  const [winCondition, setWinCondition] = useState(5);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connectToRoom = useCallback((roomId, playerName) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }

    setConnectionStatus('connecting');
    setError(null);
    reconnectAttempts.current = 0;

    const wsUrl = `${config.getWebSocketUrl()}/ws/room/${roomId}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setIsConnected(true);
        setConnectionStatus('connected');
        
        // Join the game
        wsRef.current.send(JSON.stringify({
          type: 'join',
          playerName: playerName
        }));
      };
      
      wsRef.current.onmessage = (event) => {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      };
      
      wsRef.current.onclose = () => {
        setIsConnected(false);
        setConnectionStatus('disconnected');
        
        // Auto-reconnect logic
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            connectToRoom(roomId, playerName);
          }, 2000 * reconnectAttempts.current);
        }
      };
      
      wsRef.current.onerror = (error) => {
        setError('Connection failed');
        setConnectionStatus('error');
      };
      
    } catch (err) {
      setError('Failed to connect to game server');
      setConnectionStatus('error');
    }
  }, []);

  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'joined':
        setPlayerId(message.playerId);
        setPlayerColor(message.playerColor);
        if (message.roomData) {
          updateGameFromRoomData(message.roomData);
        }
        break;
        
      case 'playerJoined':
        if (message.roomData) {
          updateGameFromRoomData(message.roomData);
        }
        break;
        
      case 'gameStarted':
        setGameState(message.gameState);
        setCurrentTurn(message.currentTurn);
        setGameStatus({ isGameOver: false });
        setLastMove(null); // Reset last move when game starts
        if (message.roomData) {
          updateGameFromRoomData(message.roomData);
        }
        break;
        
      case 'move':
        setGameState(message.gameState);
        setCurrentTurn(message.currentTurn);
        setGameStatus(message.gameStatus || { isGameOver: false });
        setLastMove(message.move); // Update last move
        break;
        
      case 'gameEnded':
        setGameStatus({
          isGameOver: true,
          winner: message.winner,
          reason: message.reason,
          winningLine: message.winningLine
        });
        break;
        
      case 'chatMessage':
        setChatMessages(prev => [...prev, message.message]);
        break;
        
      case 'playerDisconnected':
        // Handle player disconnection
        break;
        
      case 'error':
        setError(message.message);
        break;
        
      default:
        break;
    }
  };

  const updateGameFromRoomData = (roomData) => {
    setPlayers(roomData.players || {});
    setGameState(roomData.gameState);
    setCurrentTurn(roomData.currentTurn || 'X');
    setBoardSize(roomData.boardSize || 15);
    setWinCondition(roomData.winCondition || 5);
    setChatMessages(roomData.chatMessages || []);
    
    if (roomData.status === 'finished') {
      setGameStatus({
        isGameOver: true,
        winner: roomData.winner,
        reason: roomData.endReason,
        winningLine: roomData.winningLine
      });
    }
  };

  const makeMove = useCallback((row, col) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'move',
        move: { row, col }
      }));
    }
  }, []);

  const sendChatMessage = useCallback((text) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && text.trim()) {
      wsRef.current.send(JSON.stringify({
        type: 'chat',
        text: text.trim()
      }));
    }
  }, []);

  const resign = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'resign'
      }));
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return {
    gameState,
    isConnected,
    connectionStatus,
    error,
    playerColor,
    currentTurn,
    gameStatus,
    players,
    chatMessages,
    playerId,
    boardSize,
    winCondition,
    lastMove, // Expose last move
    makeMove,
    sendChatMessage,
    resign,
    connectToRoom
  };
};

export default useCaroGame;
